(() => {
  const LOADER_SELECTOR = '[data-hpf-thumbnail-loader]';
  const ROOT_SELECTOR = '[id^="HobbytechPartsFinder-"]';
  const FOCUSABLE_SELECTOR = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  function findPartsFinder(loader) {
    const loaderSection = loader.closest('.shopify-section');
    let sibling = loaderSection ? loaderSection.nextElementSibling : null;

    while (sibling) {
      const root = sibling.matches?.(ROOT_SELECTOR)
        ? sibling
        : sibling.querySelector?.(ROOT_SELECTOR);
      if (root) return root;
      sibling = sibling.nextElementSibling;
    }

    return document.querySelector(ROOT_SELECTOR);
  }

  function initialiseThumbnailMode(loader) {
    if (!loader || loader.dataset.hpfThumbnailInitialised === 'true') return;

    const root = findPartsFinder(loader);
    if (!root) return;

    const finder = root.querySelector('.hpf-finder');
    const tabs = Array.from(root.querySelectorAll('[data-hpf-tab]'));
    const panels = Array.from(root.querySelectorAll('[data-hpf-panel]'));
    const panelsContainer = root.querySelector('.hpf-panels');

    if (!finder || !tabs.length || !panels.length || !panelsContainer) return;

    loader.dataset.hpfThumbnailInitialised = 'true';
    root.classList.add('hpf--thumbnail-mode');

    const browser = document.createElement('div');
    browser.className = 'hpf-thumbnail-browser';

    const heading = document.createElement('h3');
    heading.className = 'hpf-thumbnail-browser__heading';
    heading.textContent = loader.dataset.heading || 'Select an exploded diagram';
    browser.appendChild(heading);

    const grid = document.createElement('div');
    grid.className = 'hpf-thumbnail-grid';
    grid.setAttribute('role', 'list');
    browser.appendChild(grid);

    finder.querySelector('.hpf-tabs')?.insertAdjacentElement('afterend', browser);

    const modal = document.createElement('div');
    modal.id = `HpfThumbnailModal-${root.id.replace(/[^a-zA-Z0-9_-]/g, '')}`;
    modal.className = 'hpf-thumbnail-modal';
    modal.hidden = true;
    modal.setAttribute('aria-hidden', 'true');

    const backdrop = document.createElement('button');
    backdrop.type = 'button';
    backdrop.className = 'hpf-thumbnail-modal__backdrop';
    backdrop.setAttribute('aria-label', 'Close exploded diagram');

    const dialog = document.createElement('div');
    dialog.className = 'hpf-thumbnail-modal__dialog';
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute('aria-labelledby', `${modal.id}-title`);
    dialog.tabIndex = -1;

    const bar = document.createElement('div');
    bar.className = 'hpf-thumbnail-modal__bar';

    const headingWrap = document.createElement('div');
    headingWrap.className = 'hpf-thumbnail-modal__heading';

    const eyebrow = document.createElement('span');
    eyebrow.className = 'hpf-thumbnail-modal__eyebrow';
    eyebrow.textContent = 'Interactive Parts Finder';

    const modalTitle = document.createElement('strong');
    modalTitle.id = `${modal.id}-title`;
    modalTitle.textContent = 'Exploded diagram';

    headingWrap.append(eyebrow, modalTitle);

    const barActions = document.createElement('div');
    barActions.className = 'hpf-thumbnail-modal__actions';

    const help = document.createElement('span');
    help.className = 'hpf-thumbnail-modal__help';
    help.textContent = 'Pinch or scroll to zoom · Drag to move';

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.className = 'hpf-thumbnail-modal__close';
    closeButton.innerHTML = '<span aria-hidden="true">×</span><span>Close</span>';
    closeButton.setAttribute('aria-label', loader.dataset.closeText || 'Close diagram');

    barActions.append(help, closeButton);
    bar.append(headingWrap, barActions);

    const modalBody = document.createElement('div');
    modalBody.className = 'hpf-thumbnail-modal__body';

    panelsContainer.classList.add('hpf-thumbnail-modal__panels');
    modalBody.appendChild(panelsContainer);
    dialog.append(bar, modalBody);
    modal.append(backdrop, dialog);
    root.appendChild(modal);

    const thumbnailButtons = [];
    let returnFocus = null;
    let pageScrollY = 0;
    let hideTimer = null;

    const productDialogOpen = () => Boolean(root.querySelector('.hpf-dialog[open]'));
    const modalIsOpen = () => modal.classList.contains('is-open') && !modal.hidden;

    function lockPage() {
      pageScrollY = window.scrollY || window.pageYOffset || 0;
      document.documentElement.classList.add('hpf-thumbnail-modal-open');
      document.body.classList.add('hpf-thumbnail-modal-open');
      document.body.style.top = `-${pageScrollY}px`;
    }

    function unlockPage() {
      document.documentElement.classList.remove('hpf-thumbnail-modal-open');
      document.body.classList.remove('hpf-thumbnail-modal-open');
      document.body.style.top = '';
      window.scrollTo(0, pageScrollY);
    }

    function resetSelection() {
      panels.forEach((panel) => {
        panel.hidden = true;
        panel.classList.remove('is-active');
      });

      tabs.forEach((tab) => {
        tab.classList.remove('is-active');
        tab.setAttribute('aria-selected', 'false');
        tab.tabIndex = -1;
      });

      thumbnailButtons.forEach((button) => {
        button.classList.remove('is-active');
        button.setAttribute('aria-expanded', 'false');
      });
    }

    function openDiagram(button, tab, panel) {
      if (hideTimer) window.clearTimeout(hideTimer);
      returnFocus = button;
      resetSelection();

      tab.click();
      panel.hidden = false;
      panel.classList.add('is-active');
      button.classList.add('is-active');
      button.setAttribute('aria-expanded', 'true');
      modalTitle.textContent = tab.textContent.trim();

      modal.hidden = false;
      modal.setAttribute('aria-hidden', 'false');
      lockPage();

      requestAnimationFrame(() => {
        modal.classList.add('is-open');
        requestAnimationFrame(() => {
          closeButton.focus({ preventScroll: true });
          window.dispatchEvent(new Event('resize'));
        });
      });
    }

    function closeDiagram({ restoreFocus = true } = {}) {
      if (!modalIsOpen()) return;

      root.querySelectorAll('.hpf-dialog[open]').forEach((item) => item.close?.());
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      unlockPage();
      resetSelection();

      hideTimer = window.setTimeout(() => {
        modal.hidden = true;
      }, 210);

      if (restoreFocus && returnFocus?.focus) {
        requestAnimationFrame(() => returnFocus.focus({ preventScroll: true }));
      }
    }

    tabs.forEach((tab) => {
      const panelId = tab.getAttribute('aria-controls');
      const panel = panelId ? document.getElementById(panelId) : null;
      const image = panel?.querySelector('.hpf-viewer__image');
      if (!panel || !image) return;

      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'hpf-diagram-thumb';
      button.setAttribute('role', 'listitem');
      button.setAttribute('aria-controls', modal.id);
      button.setAttribute('aria-haspopup', 'dialog');
      button.setAttribute('aria-expanded', 'false');

      const media = document.createElement('span');
      media.className = 'hpf-diagram-thumb__media';

      const thumbImage = image.cloneNode(false);
      thumbImage.className = 'hpf-diagram-thumb__image';
      thumbImage.removeAttribute('loading');
      thumbImage.loading = 'lazy';
      thumbImage.alt = image.alt || tab.textContent.trim();
      media.appendChild(thumbImage);

      const zoomIcon = document.createElement('span');
      zoomIcon.className = 'hpf-diagram-thumb__zoom';
      zoomIcon.setAttribute('aria-hidden', 'true');
      zoomIcon.textContent = '⌕';
      media.appendChild(zoomIcon);

      const body = document.createElement('span');
      body.className = 'hpf-diagram-thumb__body';

      const label = document.createElement('span');
      label.className = 'hpf-diagram-thumb__label';
      label.textContent = tab.textContent.trim();

      const action = document.createElement('span');
      action.className = 'hpf-diagram-thumb__action';
      action.textContent = loader.dataset.openText || 'Open diagram';

      body.append(label, action);
      button.append(media, body);
      grid.appendChild(button);
      thumbnailButtons.push(button);

      button.addEventListener('click', () => openDiagram(button, tab, panel));
    });

    backdrop.addEventListener('click', () => closeDiagram());
    closeButton.addEventListener('click', () => closeDiagram());

    modal.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (productDialogOpen()) return;
        event.preventDefault();
        closeDiagram();
        return;
      }

      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter((item) => {
        const style = window.getComputedStyle(item);
        return !item.hidden && style.display !== 'none' && style.visibility !== 'hidden';
      });

      if (!focusable.length) {
        event.preventDefault();
        dialog.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });

    document.addEventListener('shopify:section:unload', (event) => {
      if (event.target.contains(root) && modalIsOpen()) {
        closeDiagram({ restoreFocus: false });
      }
    });

    resetSelection();
  }

  function scan(scope = document) {
    const loaders = scope.matches?.(LOADER_SELECTOR)
      ? [scope]
      : Array.from(scope.querySelectorAll?.(LOADER_SELECTOR) || []);

    loaders.forEach(initialiseThumbnailMode);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => scan());
  } else {
    scan();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target));
})();
